import useSWR from 'swr';
import api from '../../services/api';

export interface EmailRuleCondition {
  field: 'from' | 'to' | 'subject' | 'body' | 'folder';
  operator: 'contains' | 'equals' | 'startsWith' | 'endsWith';
  value: string;
}

export interface EmailRule {
  id: number;
  userId: number;
  emailAccountId?: number | null;
  name?: string | null;
  conditions: { match: 'all' | 'any'; rules: EmailRuleCondition[] };
  actions: {
    actions: Array<
      | { type: 'copyToFolder'; personalFolderId: number }
      | { type: 'copyToFolder'; folder: string }
      | { type: 'moveToFolder'; folder: string }
      | { type: 'markRead' }
      | { type: 'addTag'; tagName: string }
      | { type: 'assignUser'; assignedToUserId: number }
      | { type: 'setWorkflowStatus'; workflowStatus: 'open' | 'working' | 'closed' }
    >;
  };
  enabled: boolean;
  priority: number;
  createdAt: string;
  updatedAt: string;
}

export type EmailRuleFormData = Omit<EmailRule, 'id' | 'userId' | 'createdAt' | 'updatedAt'>;

export const EMAIL_RULES_LIST_KEY = '/email-rules';

function normalizeEmailRulesList(body: unknown): EmailRule[] {
  if (Array.isArray(body)) return body;
  return [];
}

export const useEmailRules = (accountId?: number) => {
  const key =
    accountId != null ? `/email-rules?accountId=${accountId}` : EMAIL_RULES_LIST_KEY;
  const { data, error, isLoading, isValidating, mutate } = useSWR<EmailRule[]>(
    key,
    async (url: string) => {
      const res = await api.get(url);
      return normalizeEmailRulesList(res.data);
    },
    {
      revalidateOnMount: true,
      revalidateIfStale: true,
    },
  );
  return {
    data: data ?? [],
    isLoading,
    isValidating,
    isError: error,
    mutate,
  };
};

export const useEmailRule = (id: number) => {
  const { data, error, isLoading, mutate } = useSWR<EmailRule>(
    id > 0 ? `/email-rules/${id}` : null,
    async (url: string) => {
      const res = await api.get(url);
      return res.data;
    },
    {
      revalidateOnMount: true,
      revalidateIfStale: true,
    },
  );
  return { data, isLoading, isError: error, mutate };
};
